import { useState } from 'react'
import { X } from 'lucide-react'

import Button from '@/components/ui/Button'
import { cn } from '@/lib/cn'

type Platform = 'ios' | 'android' | 'desktop'

interface GpsDeniedModalProps {
  onClose: () => void
  onRetry: () => void
}

const instructions: Record<Platform, { label: string; steps: string[] }> = {
  ios: {
    label: 'iOS Safari',
    steps: [
      'Otwórz Ustawienia → Safari → Lokalizacja.',
      'Wybierz „Zezwól" dla tej witryny.',
      'Sprawdź też Ustawienia → Prywatność i Bezpieczeństwo → Usługi lokalizacyjne (powinny być włączone).',
      'Wróć do tej strony i kliknij „Spróbuj ponownie".',
    ],
  },
  android: {
    label: 'Android Chrome',
    steps: [
      'Kliknij trzy kropki w prawym górnym rogu przeglądarki.',
      'Wybierz „Ustawienia witryny" → „Lokalizacja".',
      'Wybierz „Zezwól".',
      'Wróć do tej strony i kliknij „Spróbuj ponownie".',
    ],
  },
  desktop: {
    label: 'Desktop (Chrome / Firefox)',
    steps: [
      'Kliknij ikonę kłódki w pasku adresu obok URL.',
      'Otwórz „Uprawnienia witryny" → „Lokalizacja".',
      'Wybierz „Zezwól", a następnie odśwież stronę.',
      'Po odświeżeniu kliknij „Spróbuj ponownie".',
    ],
  },
}

export default function GpsDeniedModal({ onClose, onRetry }: GpsDeniedModalProps) {
  const [platform, setPlatform] = useState<Platform>('desktop')
  const current = instructions[platform]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-bg-canvas/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gps-denied-title"
    >
      <div className="relative w-full max-w-[520px] rounded-card border border-border-subtle bg-bg-elevated p-6 sm:p-8">
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij"
          className="absolute top-3 right-3 text-text-muted hover:text-text-primary transition"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        <h2 id="gps-denied-title" className="text-xl font-semibold mb-2">
          Lokalizacja zablokowana
        </h2>
        <p className="text-sm text-text-muted leading-relaxed mb-5">
          Aby ewidencja czasu pracy mogła działać, potrzebujemy dostępu do GPS.
          Włącz lokalizację w ustawieniach przeglądarki dla tej strony.
        </p>

        <div className="flex gap-2 mb-4" role="tablist">
          {(Object.keys(instructions) as Platform[]).map((p) => {
            const active = p === platform
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPlatform(p)}
                className={cn(
                  'flex-1 rounded-pill px-3 py-1.5 text-xs font-medium transition',
                  active
                    ? 'bg-brand-dark text-bg-elevated'
                    : 'bg-bg-surface text-text-muted hover:text-text-primary border border-border-subtle',
                )}
              >
                {instructions[p].label}
              </button>
            )
          })}
        </div>

        <ol className="space-y-2 text-sm text-text-primary list-decimal pl-5">
          {current.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>

        <div className="mt-6 flex gap-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Zamknij
          </Button>
          <Button type="button" className="flex-1" onClick={onRetry}>
            Spróbuj ponownie
          </Button>
        </div>
      </div>
    </div>
  )
}
